import styled from '@emotion/styled';
import { Checkbox, Field } from '@hotjar/hotsauce';
import { vatAppliesToCountry } from '@hotjar/webapp/billing/tax';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { DisclaimerDisplayCosts } from '@hotjar/checkout/src/PaymentDetails/utils/displayCosts';
import type { PaymentMethodType } from '../../types';

const S = {
  Field: styled(Field)`
    width: 100%;
  `,
};

interface Props {
  billingCycle?: IBillingCycle;
  paymentMethodType: PaymentMethodType;
  countryIsoCode?: string;
  disclaimerDisplayCosts?: DisclaimerDisplayCosts;
  className?: string;
}

export interface AgreeNoticeFormField {
  agreeNotice: string;
}

export function AgreeNotice({
  billingCycle,
  paymentMethodType,
  countryIsoCode,
  disclaimerDisplayCosts,
  className,
}: Props): JSX.Element | null {
  const { formState, control } = useFormContext<AgreeNoticeFormField>();
  const { t: tBilling } = useTranslationWithPrefix('billing:payment-details');
  const showNotice =
    billingCycle === 'yearly_recurring' &&
    disclaimerDisplayCosts?.isVisible &&
    paymentMethodType !== 'paypal';

  if (!showNotice || !disclaimerDisplayCosts) {
    return null;
  }

  let labelContent: string;

  const isVatCountry = vatAppliesToCountry(countryIsoCode);

  const translationParameters = {
    payNow: disclaimerDisplayCosts.totalCurrent,
    payLater: disclaimerDisplayCosts.totalFuture,
  };
  if (!disclaimerDisplayCosts.isProrated) {
    labelContent = isVatCountry
      ? tBilling('form:notice-vat', translationParameters)
      : tBilling('form:notice-no-vat', translationParameters);
  } else {
    labelContent = isVatCountry
      ? tBilling('form:notice-prorata-vat', translationParameters)
      : tBilling('form:notice-prorata-no-vat', translationParameters);
  }
  const fieldId = 'agreeNotice';

  return (
    <Controller
      control={control}
      name={fieldId}
      defaultValue={false}
      rules={{ required: tBilling<string>('form:error-required') }}
      render={({ onChange, value }) => (
        <S.Field
          isInvalid={!!formState.errors.agreeNotice}
          invalidText={formState.errors?.agreeNotice?.message}
          inputId={fieldId}
          labelText=""
          className={className}
        >
          <Checkbox
            // We use dangerouslySetInnerHTML, because React by default escapes HTML
            //  tags, which are part of string (translate function returns strings)
            // eslint-disable-next-line react/no-danger
            labelContent={<span dangerouslySetInnerHTML={{ __html: labelContent }} />} // nosemgrep
            name={fieldId}
            id={fieldId}
            value="true"
            isRequired
            isChecked={value}
            onChange={() => {
              onChange(!value);
            }}
          />
        </S.Field>
      )}
    />
  );
}
